<div class="d-flex flex-column gap-3" data-testid="licences-panel">
  <div class="d-flex flex-wrap align-items-start justify-content-between gap-3">
    <div class="d-flex flex-column gap-1">
      <h3 class="av-section-title m-0">{{ 'backoffice.licences.panel.title' | translate }}</h3>
      <p class="av-meta m-0">{{ 'backoffice.licences.hint' | translate }}</p>

      <a
        class="av-btn text-decoration-none align-self-start"
        [routerLink]="['/licences']"
        [queryParams]="{ organization: organizationId() }"
        data-testid="licence-portfolio-link"
      >
        <mat-icon aria-hidden="true">confirmation_number</mat-icon>
        <span>{{ 'backoffice.licences.panel.portfolio_link' | translate }}</span>
      </a>
    </div>

    @if (facade.canAdjust() && facade.hasSubscription()) {
      <div class="d-flex gap-2">
        <button
          type="button"
          class="av-btn av-btn-primary"
          [disabled]="facade.isMutating() || !facade.isAdjustable()"
          (click)="openAdjust()"
        >
          <mat-icon aria-hidden="true">tune</mat-icon>
          {{ 'backoffice.licences.actions.adjust_quantity' | translate }}
        </button>

        <button
          type="button"
          class="av-btn"
          [disabled]="facade.isMutating() || !hasPendingCancellation()"
          (click)="openResume()"
        >
          <mat-icon aria-hidden="true">restart_alt</mat-icon>
          {{ 'backoffice.licences.actions.resume_renewal' | translate }}
        </button>
      </div>
    }
  </div>

  @if (!facade.canAdjust()) {
    <p class="av-meta m-0" data-testid="licences-restricted">{{ 'backoffice.licences.notes.restricted' | translate }}</p>
  }

  @switch (facade.state()) {
    @case ('error') {
      <av-empty-state
        icon="error_outline"
        [title]="'backoffice.licences.error.title' | translate"
        [message]="'backoffice.licences.error.message' | translate"
      ></av-empty-state>
    }
    @case ('ready') {
      @if (!facade.hasSubscription()) {
        <av-empty-state
          icon="confirmation_number"
          [title]="'backoffice.licences.empty.no_subscription_title' | translate"
          [message]="'backoffice.licences.empty.no_subscription_message' | translate"
        ></av-empty-state>
      } @else {
        <div class="component-grid" data-testid="licence-components">
          @for (component of facade.components(); track component.component_type) {
            <div class="component-card p-3 d-flex flex-column gap-2">
              <div class="d-flex align-items-center justify-content-between gap-2">
                <span class="cell-strong">{{ component.componentLabelKey | translate }}</span>

                @if (component.is_available) {
                  <av-status-chip
                    [label]="'backoffice.licences.chips.priced' | translate"
                    tone="positive"
                  ></av-status-chip>
                } @else {
                  <av-status-chip
                    [label]="'backoffice.licences.chips.absent' | translate"
                    tone="neutral"
                  ></av-status-chip>
                }
              </div>

              <span class="av-meta">{{ component.stripeProductNameKey | translate }}</span>

              @if (!component.is_available) {
                <p class="av-meta m-0" [attr.data-testid]="'component-absent-' + component.component_type">
                  {{ component.isAgent ? ('backoffice.licences.notes.agent_absent' | translate) : '' }}
                </p>
              } @else {
                <dl class="component-stats m-0">
                  <dt class="av-meta">{{ 'backoffice.licences.table.purchased' | translate }}</dt>
                  <dd>{{ component.purchased }}</dd>

                  <dt class="av-meta">{{ 'backoffice.licences.table.assigned' | translate }}</dt>
                  <dd>{{ component.assigned }}</dd>

                  <dt class="av-meta">{{ 'backoffice.licences.table.free' | translate }}</dt>
                  <dd>{{ component.free }}</dd>

                  <dt class="av-meta">{{ 'backoffice.licences.table.ending' | translate }}</dt>
                  <dd>{{ component.pending_cancellation }}</dd>

                  <dt class="av-meta">{{ 'backoffice.licences.table.stripe_quantity' | translate }}</dt>
                  <dd>{{ component.stripe_quantity ?? unresolved }}</dd>

                  <dt class="av-meta">{{ 'backoffice.licences.table.unit_amount' | translate }}</dt>
                  <dd>
                    {{
                      component.unit_amount === null
                        ? unresolved
                        : (component.unit_amount | number: '1.2-2')
                    }}
                    {{ component.currency || '' }}
                  </dd>
                </dl>

                @if (component.isDrifted) {
                  <div class="av-banner p-2 d-flex align-items-start gap-2" data-testid="licence-drift">
                    <mat-icon aria-hidden="true">warning</mat-icon>
                    <span>{{ 'backoffice.licences.notes.drift' | translate }}</span>
                  </div>
                }

                @if (component.half_state > 0) {
                  <div class="av-banner p-2 d-flex align-items-start gap-2" data-testid="licence-half-state">
                    <mat-icon aria-hidden="true">help_outline</mat-icon>
                    <span>{{ 'backoffice.licences.notes.half_state' | translate }}</span>
                  </div>
                }
              }
            </div>
          }
        </div>

        @if (hasPendingCancellation()) {
          <div class="av-banner p-3 d-flex align-items-start gap-2" data-testid="licence-pending-note">
            <mat-icon aria-hidden="true">event_busy</mat-icon>
            <span>
              {{ 'backoffice.licences.notes.pending_cancellation' | translate }}
              {{ 'backoffice.licences.notes.cancels_at_scheduled' | translate }}
            </span>
          </div>
        }

        <div class="filter-row d-flex flex-wrap align-items-end gap-2">
          <label class="d-flex flex-column gap-1" for="licence-component-filter">
            <span class="av-meta">{{ 'backoffice.licences.filters.component' | translate }}</span>
            <select
              id="licence-component-filter"
              class="av-field"
              [value]="facade.filters().component_type"
              (change)="filterComponent($any($event.target).value)"
            >
              <option value="">{{ 'backoffice.licences.filters.all_components' | translate }}</option>
              @for (option of componentOptions; track option) {
                <option [value]="option">{{ componentLabelKeys[option] | translate }}</option>
              }
            </select>
          </label>

          <label class="d-flex flex-column gap-1" for="licence-holder-filter">
            <span class="av-meta">{{ 'backoffice.licences.filters.holder' | translate }}</span>
            <select
              id="licence-holder-filter"
              class="av-field"
              [value]="facade.filters().holder"
              (change)="filterHolder($any($event.target).value)"
            >
              <option value="">{{ 'backoffice.licences.filters.any_holder' | translate }}</option>
              @for (option of holderOptions; track option) {
                <option [value]="option">{{ holderLabelKeys[option] | translate }}</option>
              }
            </select>
          </label>

          <label class="d-flex flex-column gap-1" for="licence-state-filter">
            <span class="av-meta">{{ 'backoffice.licences.filters.state' | translate }}</span>
            <select
              id="licence-state-filter"
              class="av-field"
              [value]="facade.filters().state"
              (change)="filterState($any($event.target).value)"
            >
              <option value="">{{ 'backoffice.licences.filters.any_state' | translate }}</option>
              @for (option of stateOptions; track option) {
                <option [value]="option">{{ stateLabelKeys[option] | translate }}</option>
              }
            </select>
          </label>

          @if (facade.hasActiveFilters()) {
            <button type="button" class="av-btn" (click)="clearFilters()">
              {{ 'backoffice.licences.filters.clear' | translate }}
            </button>
          }
        </div>

        @if (isEmpty()) {
          <av-empty-state
            icon="confirmation_number"
            [title]="
              (facade.hasActiveFilters()
                ? 'backoffice.licences.empty.filtered_title'
                : 'backoffice.licences.empty.title'
              ) | translate
            "
            [message]="
              (facade.hasActiveFilters()
                ? 'backoffice.licences.empty.filtered_message'
                : 'backoffice.licences.empty.message'
              ) | translate
            "
          ></av-empty-state>
        } @else {
          <div class="table-scroll">
            <table class="av-table w-100" data-testid="licence-table">
              <thead>
                <tr>
                  <th scope="col">{{ 'backoffice.licences.table.component' | translate }}</th>
                  <th scope="col">{{ 'backoffice.licences.table.state' | translate }}</th>
                  <th scope="col">{{ 'backoffice.licences.table.holder' | translate }}</th>
                  <th scope="col">{{ 'backoffice.licences.table.purchased' | translate }}</th>
                  <th scope="col">{{ 'backoffice.licences.table.assigned' | translate }}</th>
                  <th scope="col">{{ 'backoffice.licences.table.ends' | translate }}</th>
                  <th scope="col" class="text-end">{{ 'backoffice.licences.table.actions' | translate }}</th>
                </tr>
              </thead>

              <tbody>
                @for (licence of facade.licences(); track licence.id) {
                  <tr>
                    <td class="cell-strong">{{ licence.componentLabelKey | translate }}</td>

                    <td>
                      <av-status-chip
                        [label]="licence.stateLabelKey | translate"
                        [tone]="licence.stateTone"
                      ></av-status-chip>
                    </td>

                    <td>
                      <div class="d-flex flex-column">
                        <span>{{ licence.holderDisplay }}</span>
                        <span class="av-meta">{{ licence.holderLabelKey | translate }}</span>
                        @if (licence.holderIsDeleted) {
                          <span class="av-meta">{{ 'backoffice.licences.table.holder_deleted_short' | translate }}</span>
                        }
                      </div>
                    </td>

                    <td class="cell-muted">
                      {{
                        licence.purchased_at
                          ? (licence.purchased_at | date: dateTimeFormat)
                          : unresolved
                      }}
                    </td>

                    <td class="cell-muted">
                      {{
                        licence.assigned_at ? (licence.assigned_at | date: dateTimeFormat) : unresolved
                      }}
                    </td>

                    <td class="cell-muted">
                      @if (licence.isEnding) {
                        <span [matTooltip]="'backoffice.licences.notes.cancels_at_scheduled' | translate">
                          {{ licence.cancels_at | date: dateTimeFormat }}
                        </span>
                      } @else {
                        {{ unresolved }}
                      }
                    </td>

                    <td class="text-end">
                      @if (facade.canAdjust()) {
                        @if (licence.isResumable) {
                          <button
                            type="button"
                            class="av-btn"
                            [matTooltip]="'backoffice.licences.resume_one.title' | translate"
                            [disabled]="facade.isMutating()"
                            (click)="resumeOne(licence)"
                          >
                            <mat-icon aria-hidden="true">restart_alt</mat-icon>
                          </button>
                        } @else if (licence.isCancellable) {
                          <button
                            type="button"
                            class="av-btn"
                            [matTooltip]="
                              'backoffice.licences.cancel.tooltip'
                                | translate
                                  : {
                                      title: 'backoffice.licences.cancel.title' | translate,
                                      note: 'backoffice.licences.notes.pending_cancellation' | translate,
                                    }
                            "
                            [disabled]="facade.isMutating()"
                            (click)="cancelOne(licence)"
                          >
                            <mat-icon aria-hidden="true">event_busy</mat-icon>
                          </button>
                        }
                      }
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>

          <av-paginator
            [page]="facade.page()"
            [pageSize]="facade.pageSize()"
            [total]="facade.total()"
            [disabled]="facade.isMutating()"
            (pageChange)="goToPage($event)"
            (pageSizeChange)="changePageSize($event)"
          ></av-paginator>
        }
      }
    }
    @default {
      <av-empty-state
        icon="hourglass_empty"
        [title]="'backoffice.licences.panel.loading_title' | translate"
        [message]="'backoffice.licences.panel.loading_message' | translate"
      ></av-empty-state>
    }
  }
</div>
